import { Colors } from "@/enums/Colors";
import { useResponsive } from "@/hooks/useResponsive";
import { MenuItem } from "@/types/MenuItem";
import { Menu, menuClasses, menuItemClasses, MenuItem as MUIMenuItem, styled, typographyClasses } from "@mui/material";
import React, { ReactNode, useEffect, useRef, useState } from "react";

const StyledMenu = styled(Menu)(() => ({
  [`& .${menuClasses.list}`]: {
    padding: 0,
    width: "fit-content",
  },
  [`& .${menuClasses.paper}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    borderRadius: "0.25rem",
    border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    [`& .${menuItemClasses.root}`]: {
      display: "flex",
      padding: "0.375rem 0.75rem",
      gap: "0.5rem",
      alignItems: "center",
      alignSelf: "stretch",
      minHeight: "auto",
      [`& .${typographyClasses.root}`]: {
        fontWeight: 400,
        fontSize: "0.875rem",
      },
    },
  },
}));

interface LazoContextMenuProps {
  children: ReactNode;
  menuItems: MenuItem[];
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  triggerOnClick?: boolean; // If true, opens on click instead of long press for mobile
}

function LazoContextMenu({ children, menuItems, disabled = false, onOpen, onClose, triggerOnClick = false }: LazoContextMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [touchTimer, setTouchTimer] = useState<NodeJS.Timeout | null>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  const open = Boolean(anchorEl);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      // Create a virtual anchor at the click position
      const virtualAnchor = {
        nodeType: 1,
        getBoundingClientRect: () => ({
          top: event.clientY,
          left: event.clientX,
          bottom: event.clientY,
          right: event.clientX,
          width: 0,
          height: 0,
          x: event.clientX,
          y: event.clientY,
          toJSON: () => ({}),
        }),
      } as any;
      setAnchorEl(virtualAnchor);
      onOpen?.();
    }
    return false;
  };

  const handleClick = (event: React.MouseEvent) => {
    if (triggerOnClick && !disabled) {
      event.preventDefault();
      event.stopPropagation();

      // Trigger haptic feedback if available on mobile
      if (isMobileOrTablet && window.navigator && "vibrate" in window.navigator) {
        window.navigator.vibrate(50);
      }

      // Create a virtual anchor at the click position
      const virtualAnchor = {
        nodeType: 1,
        getBoundingClientRect: () => ({
          top: event.clientY,
          left: event.clientX,
          bottom: event.clientY,
          right: event.clientX,
          width: 0,
          height: 0,
          x: event.clientX,
          y: event.clientY,
          toJSON: () => ({}),
        }),
      } as any;
      setAnchorEl(virtualAnchor);
      onOpen?.();
    }
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (isMobileOrTablet && !disabled && !triggerOnClick) {
      // Only use long press if triggerOnClick is false
      // Store the touch position
      const touch = event.touches[0];

      const timer = setTimeout(() => {
        // Prevent the default context menu
        if (event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();

        // Trigger haptic feedback if available
        if (window.navigator && "vibrate" in window.navigator) {
          window.navigator.vibrate(50);
        }

        // Create a virtual anchor at the touch position
        const virtualAnchor = {
          nodeType: 1,
          getBoundingClientRect: () => ({
            top: touch.clientY,
            left: touch.clientX,
            bottom: touch.clientY,
            right: touch.clientX,
            width: 0,
            height: 0,
            x: touch.clientX,
            y: touch.clientY,
            toJSON: () => ({}),
          }),
        } as any;
        setAnchorEl(virtualAnchor);
        onOpen?.();
      }, 500); // 500ms hold duration
      setTouchTimer(timer);
    }
  };

  const handleTouchEnd = () => {
    if (touchTimer && !triggerOnClick) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
  };

  const handleTouchMove = () => {
    if (touchTimer && !triggerOnClick) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleMenuItemClick = (event: React.MouseEvent, item: MenuItem) => {
    event.stopPropagation();
    item.onClick(event);
    handleClose();
  };

  useEffect(() => {
    // Prevent context menu on the whole component
    const handleGlobalContextMenu = (e: MouseEvent) => {
      if (childRef.current && childRef.current.contains(e.target as Node)) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("contextmenu", handleGlobalContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleGlobalContextMenu);
      if (touchTimer) {
        clearTimeout(touchTimer);
      }
    };
  }, [touchTimer]);

  return (
    <>
      <div
        ref={childRef}
        onContextMenu={handleContextMenu}
        onClick={triggerOnClick ? handleClick : undefined}
        onTouchStart={!triggerOnClick ? handleTouchStart : undefined}
        onTouchEnd={!triggerOnClick ? handleTouchEnd : undefined}
        onTouchMove={!triggerOnClick ? handleTouchMove : undefined}
        onMouseDown={(e) => {
          // Prevent text selection on long press or if using click trigger
          if (isMobileOrTablet && (!triggerOnClick || (triggerOnClick && e.button === 0))) {
            e.preventDefault();
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: isMobileOrTablet ? "none" : "auto",
          cursor: isMobileOrTablet && triggerOnClick ? "pointer" : undefined,
        }}
        data-lazo-context-menu="true"
      >
        {children}
      </div>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{ onMouseLeave: handleClose }}
        disableScrollLock={true}
      >
        {menuItems.flatMap((item, index) => {
          const elements = [
            <MUIMenuItem
              key={item.id || `item-${index}`}
              onClick={(e) => handleMenuItemClick(e, item)}
              disabled={item.disabled}
              sx={{ gap: ".5rem" }}
            >
              {item.label}
            </MUIMenuItem>,
          ];
          if (item.divider && index < menuItems.length - 1) {
            elements.push(
              <div key={`divider-${index}`} style={{ borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`, margin: "0.25rem 0" }} />
            );
          }
          return elements;
        })}
      </StyledMenu>
    </>
  );
}

export default LazoContextMenu;
export type { MenuItem as ContextMenuItem } from "@/types/MenuItem";
